import {
    CheckCircleRounded as CheckCircleRoundedIcon,
    EditOutlined as EditOutlinedIcon,
    WarningAmberRounded as WarningAmberRoundedIcon,
} from "@mui/icons-material";
import { Alert, Box, Container, Skeleton, Typography } from "@mui/material";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxButton from "components/UI/AcxButton";
import AcxChip from "components/UI/AcxChip";
import AcxConfirmationDialog from "components/UI/AcxConfirmationDialog";
import AcxDataGrid from "components/UI/AcxDataGrid/AcxDataGrid";
import AcxDataGridStore from "components/UI/AcxDataGrid/AcxDataGridStore";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import AcxHierarchySelector from "components/UI/AcxHierarchySelector/AcxHierarchySelector";
import AcxTextField from "components/UI/AcxTextField";
import IColDef from "components/UI/AcxDataGrid/IColDef";
import { observer } from "mobx-react-lite";
import React from "react";
import { ServiceError } from "services/BaseService";
import {
    insightTypeToTitle,
    ProactiveInsightsService,
} from "services/ProactiveInsightsService";
import type {
    ProactiveInsightDefinition,
    ProactiveInsightRunDetails,
    ProactiveInsightSettings,
} from "services/ProactiveInsightsService";
import { AuthStore } from "stores/AuthStore";
import theme from "Theme/AppTheme";
import { useStore } from "utils/useStore";

type Props = {
    id: string;
};

type DefinitionRow = ProactiveInsightDefinition;

const proactiveInsightsService = new ProactiveInsightsService();
const DEFAULT_RUN_TIME = "03:00";

function getErrorMessage(error: unknown) {
    if (error instanceof ServiceError) {
        return error.errorMessage ?? error.message;
    }

    return error instanceof Error ? error.message : "Something went wrong";
}

function formatRunPeriod(start: string, end: string) {
    const startDate = new Date(`${start}T00:00:00Z`);
    const endDate = new Date(`${end}T00:00:00Z`);
    const monthFormatter = new Intl.DateTimeFormat("en-US", {
        month: "short",
        timeZone: "UTC",
    });
    const dayFormatter = new Intl.DateTimeFormat("en-US", {
        day: "numeric",
        timeZone: "UTC",
    });
    const yearFormatter = new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        timeZone: "UTC",
    });
    const startMonth = monthFormatter.format(startDate);
    const endMonth = monthFormatter.format(endDate);
    const startDay = dayFormatter.format(startDate);
    const endDay = dayFormatter.format(endDate);
    const startYear = yearFormatter.format(startDate);
    const endYear = yearFormatter.format(endDate);

    if (startYear !== endYear) {
        return `${startMonth} ${startDay}, ${startYear}–${endMonth} ${endDay}, ${endYear}`;
    }

    if (startMonth === endMonth) {
        return `${startMonth} ${startDay}–${endDay}, ${endYear}`;
    }

    return `${startMonth} ${startDay}–${endMonth} ${endDay}, ${endYear}`;
}

function normalizeRunTime(runTime?: string | null) {
    return runTime?.slice(0, 5) || DEFAULT_RUN_TIME;
}

function formatRunTime(runTime?: string | null) {
    const normalizedTime = normalizeRunTime(runTime);
    const [hour, minute] = normalizedTime.split(":").map(Number);

    return new Intl.DateTimeFormat("en-US", {
        hour: "numeric",
        minute: "2-digit",
        timeZone: "UTC",
    }).format(new Date(Date.UTC(1970, 0, 1, hour, minute)));
}

const ProactiveInsightsSkeleton = () => (
    <>
        <Box sx={{ mb: 2, width: "100%" }}>
            <Skeleton variant="text" width={220} height={28} />
            <Skeleton
                variant="rectangular"
                width="100%"
                height={36}
                sx={{
                    borderRadius: 1,
                    maxWidth: 520,
                    mt: 1,
                }}
            />
        </Box>
        <Box sx={{ width: "100%" }}>
            <Skeleton
                variant="rectangular"
                width="100%"
                height={52}
                sx={{ borderRadius: "8px 8px 0 0" }}
            />
            {Array.from({ length: 8 }).map((_, index) => (
                <Skeleton
                    key={index}
                    variant="rectangular"
                    width="100%"
                    height={42}
                    sx={{ mt: "1px" }}
                />
            ))}
        </Box>
    </>
);

export default observer(function OrganizationProactiveInsights({ id }: Props) {
    const authStore = useStore(AuthStore);
    const messageStore = useStore(MessageStore);
    const dataGridStore = React.useMemo(() => {
        const store = new AcxDataGridStore(
            "OrganizationProactiveInsights",
            "Administrative",
        );
        store.checkboxSelection = false;
        store.disableRowSelectionOnClick = true;
        store.hideFilter = true;
        store.hideVertIcon = true;
        store.disableLocalConfig = true;
        store.removeHeight = "50px";
        return store;
    }, []);

    const [definitions, setDefinitions] = React.useState<DefinitionRow[]>([]);
    const [settings, setSettings] =
        React.useState<ProactiveInsightSettings | null>(null);
    const [isScheduleModalOpen, setIsScheduleModalOpen] = React.useState(false);
    const [scheduleTime, setScheduleTime] = React.useState(DEFAULT_RUN_TIME);
    const [isUpdatingSettings, setIsUpdatingSettings] = React.useState(false);
    const [selectedHierarchyIds, setSelectedHierarchyIds] = React.useState<
        string[]
    >([]);
    const [isInitialLoading, setIsInitialLoading] = React.useState(true);
    const [isUpdatingHierarchies, setIsUpdatingHierarchies] =
        React.useState(false);
    const [runDetails, setRunDetails] =
        React.useState<ProactiveInsightRunDetails | null>(null);
    const [isSchedulingRun, setIsSchedulingRun] = React.useState(false);
    const [isRerunConfirmationOpen, setIsRerunConfirmationOpen] =
        React.useState(false);

    const loadDefinitions = React.useCallback(async () => {
        dataGridStore.setLoading(true);
        try {
            const [response, currentRunDetails, currentSettings] =
                await Promise.all([
                    proactiveInsightsService.listDefinitions(),
                    proactiveInsightsService.getRunDetails(),
                    proactiveInsightsService.getSettings(),
                ]);
            setDefinitions(response);
            setRunDetails(currentRunDetails);
            setSettings(currentSettings);
            setScheduleTime(normalizeRunTime(currentSettings.runTime));
            dataGridStore.setRows(response);
            setSelectedHierarchyIds((prev) => {
                if (prev.length > 0) {
                    return prev;
                }

                return (
                    response.find(
                        (definition) =>
                            definition.organizationStructureMemberIds.length >
                            0,
                    )?.organizationStructureMemberIds ?? []
                );
            });
        } finally {
            dataGridStore.setLoading(false);
            setIsInitialLoading(false);
        }
    }, [dataGridStore]);

    const applyHierarchyIdsToActiveDefinitions = React.useCallback(
        async (hierarchyIds?: string[]) => {
            const nextHierarchyIds = hierarchyIds ?? selectedHierarchyIds;

            if (nextHierarchyIds.length === 0) {
                return;
            }

            setIsUpdatingHierarchies(true);
            try {
                const updatedDefinitions = await Promise.all(
                    definitions.map((definition) =>
                        proactiveInsightsService.updateDefinition(
                            definition.id,
                            {
                                threshold: definition.threshold,
                                isActive: settings?.isEnabled ?? false,
                                organizationStructureMemberIds:
                                    nextHierarchyIds,
                            },
                        ),
                    ),
                );
                const updatedDefinitionById = new Map(
                    updatedDefinitions.map((definition) => [
                        definition.id,
                        definition,
                    ]),
                );

                setDefinitions((prev) => {
                    const next = prev.map((definition) => {
                        const updatedDefinition = updatedDefinitionById.get(
                            definition.id,
                        );

                        return updatedDefinition
                            ? {
                                  ...updatedDefinition,
                                  previousRunInsightCount:
                                      definition.previousRunInsightCount,
                              }
                            : definition;
                    });
                    dataGridStore.setRows(next);
                    return next;
                });
            } finally {
                setIsUpdatingHierarchies(false);
            }
        },
        [dataGridStore, definitions, selectedHierarchyIds, settings?.isEnabled],
    );

    const updateSettings = React.useCallback(
        async (isEnabled: boolean) => {
            setIsUpdatingSettings(true);
            try {
                const updatedSettings =
                    await proactiveInsightsService.updateSettings({
                        isEnabled,
                        runTime: isEnabled ? `${scheduleTime}:00` : null,
                        organizationStructureMemberIds: selectedHierarchyIds,
                    });

                setSettings(updatedSettings);
                setDefinitions((prev) =>
                    prev.map((definition) => ({
                        ...definition,
                        isActive: isEnabled,
                        organizationStructureMemberIds: selectedHierarchyIds,
                    })),
                );
                setIsScheduleModalOpen(false);
                messageStore.logMessage(
                    isEnabled
                        ? settings?.isEnabled
                            ? "Proactive Insights schedule updated."
                            : "Proactive Insights turned on."
                        : "Proactive Insights turned off.",
                    "success",
                );
            } catch (error) {
                messageStore.logError(getErrorMessage(error));
            } finally {
                setIsUpdatingSettings(false);
            }
        },
        [messageStore, scheduleTime, selectedHierarchyIds, settings?.isEnabled],
    );

    const openScheduleModal = React.useCallback(() => {
        setScheduleTime(normalizeRunTime(settings?.runTime));
        setIsScheduleModalOpen(true);
    }, [settings?.runTime]);

    const scheduleRun = React.useCallback(
        async (rerun: boolean) => {
            setIsSchedulingRun(true);
            try {
                const updatedRunDetails = await proactiveInsightsService.run(
                    rerun,
                );
                setRunDetails(updatedRunDetails);
                setDefinitions((prev) =>
                    prev.map((definition) => ({
                        ...definition,
                        previousRunInsightCount: null,
                    })),
                );
                setIsRerunConfirmationOpen(false);
                messageStore.logMessage(
                    rerun
                        ? "Proactive Insights rerun scheduled."
                        : "Proactive Insights run scheduled.",
                    "success",
                );
            } catch (error) {
                messageStore.logError(getErrorMessage(error));
            } finally {
                setIsSchedulingRun(false);
            }
        },
        [messageStore],
    );

    const updateThreshold = React.useCallback(
        async (nextRow: DefinitionRow, previousRow: DefinitionRow) => {
            if (nextRow.threshold === previousRow.threshold) {
                return previousRow;
            }

            try {
                const updatedDefinition =
                    await proactiveInsightsService.updateDefinition(
                        previousRow.id,
                        {
                            threshold: nextRow.threshold,
                            isActive: previousRow.isActive,
                            organizationStructureMemberIds:
                                previousRow.organizationStructureMemberIds,
                        },
                    );
                const mergedDefinition = {
                    ...updatedDefinition,
                    previousRunInsightCount:
                        previousRow.previousRunInsightCount,
                };

                setDefinitions((prev) =>
                    prev.map((definition) =>
                        definition.id === mergedDefinition.id
                            ? mergedDefinition
                            : definition,
                    ),
                );
                messageStore.logMessage("Threshold updated.", "success");

                return mergedDefinition;
            } catch (error) {
                messageStore.logError(getErrorMessage(error));
                throw error;
            }
        },
        [messageStore],
    );

    const hasSelectedHierarchy = selectedHierarchyIds.length > 0;
    const runPeriod = runDetails
        ? formatRunPeriod(runDetails.periodStart, runDetails.periodEnd)
        : "";
    const isRunInProgress =
        runDetails?.status === "Awaiting" || runDetails?.status === "Ranking";
    const runButtonTooltip =
        isRunInProgress && !runDetails?.canRerun
            ? "A rerun is unavailable while the current run is still in progress."
            : "";

    React.useEffect(() => {
        const columns: IColDef[] = [
            {
                headerName: "Insight Type",
                field: "insightType",
                flex: 2,
                renderCell: (params) =>
                    insightTypeToTitle[params.row.insightType] ??
                    params.row.insightType,
            },
            {
                headerName: "Threshold",
                field: "threshold",
                flex: 1,
                type: "number",
                editable: true,
                valueParser: (value) => Number(value),
                renderCell: (params) => (
                    <Box
                        onClick={(event) => {
                            event.stopPropagation();
                            params.api.startCellEditMode({
                                id: params.id,
                                field: params.field,
                            });
                        }}
                        sx={{
                            alignItems: "center",
                            cursor: "pointer",
                            display: "flex",
                            gap: 0.75,
                            height: "100%",
                            justifyContent: "flex-end",
                            width: "100%",
                            "& .threshold-edit-icon": {
                                opacity: 0,
                                transition: "opacity 120ms ease-in-out",
                            },
                            "&:hover .threshold-edit-icon": {
                                opacity: 1,
                            },
                        }}
                    >
                        <span>{params.value}</span>
                        <EditOutlinedIcon
                            className="threshold-edit-icon"
                            sx={{
                                color: theme.palette.gray[500],
                                fontSize: 15,
                            }}
                        />
                    </Box>
                ),
                preProcessEditCellProps: (params) => ({
                    ...params.props,
                    error:
                        params.props.value === "" ||
                        !Number.isFinite(Number(params.props.value)),
                }),
            },
            {
                headerName: "Insights Created",
                description:
                    "Number of insights created in the completed run shown above.",
                field: "previousRunInsightCount",
                flex: 1,
                type: "number",
                renderCell: (params) => params.value ?? "—",
            },
        ];

        dataGridStore.setColumns(columns);
    }, [dataGridStore]);

    React.useEffect(() => {
        dataGridStore.processRowUpdate = (nextRow, previousRow) =>
            updateThreshold(
                nextRow as DefinitionRow,
                previousRow as DefinitionRow,
            );
        dataGridStore.onProcessRowUpdateError = () => undefined;

        return () => {
            dataGridStore.processRowUpdate = undefined;
            dataGridStore.onProcessRowUpdateError = undefined;
        };
    }, [dataGridStore, updateThreshold]);

    React.useEffect(() => {
        loadDefinitions();
    }, [loadDefinitions]);

    React.useEffect(() => {
        dataGridStore.setRows(definitions);
    }, [dataGridStore, definitions]);

    React.useEffect(() => {
        if (
            runDetails?.status !== "Awaiting" &&
            runDetails?.status !== "Ranking"
        ) {
            return;
        }

        const intervalId = window.setInterval(async () => {
            try {
                const currentRunDetails =
                    await proactiveInsightsService.getRunDetails();

                // Platform dispatches the messages, but Orchestration owns ledger creation.
                // Keep the locally scheduled state during that short handoff window.
                if (currentRunDetails.hasRun) {
                    setRunDetails(currentRunDetails);

                    if (currentRunDetails.status === "Complete") {
                        const updatedDefinitions =
                            await proactiveInsightsService.listDefinitions();
                        setDefinitions(updatedDefinitions);
                    }
                }
            } catch {
                // The page-level error handling will surface failures on an explicit action.
                // A transient polling failure should not interrupt the user's Admin session.
            }
        }, 60000);

        return () => window.clearInterval(intervalId);
    }, [runDetails?.status]);

    return (
        <Container
            maxWidth="xl"
            sx={{ padding: theme.spacing(2), height: "100%", width: "100%" }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    minWidth: 0,
                    width: "100%",
                }}
            >
                {isInitialLoading ? (
                    <ProactiveInsightsSkeleton />
                ) : (
                    <>
                        <Box
                            sx={{
                                alignItems: {
                                    xs: "flex-start",
                                    sm: "center",
                                },
                                display: "flex",
                                flexDirection: {
                                    xs: "column",
                                    sm: "row",
                                },
                                gap: 2,
                                justifyContent: "space-between",
                                mb: 3,
                                width: "100%",
                            }}
                        >
                            <Box>
                                <Box
                                    sx={{
                                        alignItems: "center",
                                        display: "flex",
                                        gap: 1,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "18px",
                                            fontWeight: 600,
                                            lineHeight: "24px",
                                        }}
                                    >
                                        Proactive Insights Schedule
                                    </Typography>
                                    <AcxChip
                                        size="small"
                                        color={
                                            settings?.isEnabled
                                                ? "lightBlue"
                                                : "gray"
                                        }
                                        label={
                                            settings?.isEnabled ? "On" : "Off"
                                        }
                                        style={{ marginBottom: 0 }}
                                    />
                                </Box>
                                <Typography
                                    sx={{
                                        color: theme.palette.gray[500],
                                        fontSize: "14px",
                                        lineHeight: "20px",
                                        mt: 0.5,
                                    }}
                                >
                                    {settings?.isEnabled
                                        ? `Runs every Monday at ${formatRunTime(
                                              settings.runTime,
                                          )} Eastern Time.`
                                        : "Turn on Proactive Insights to run every insight on a weekly schedule."}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexShrink: 0,
                                    gap: 1,
                                }}
                            >
                                {settings?.isEnabled ? (
                                    <>
                                        <AcxButton
                                            variant="text"
                                            onClick={openScheduleModal}
                                            disabled={isUpdatingSettings}
                                        >
                                            Update time
                                        </AcxButton>
                                        <AcxButton
                                            variant="outlined"
                                            onClick={() =>
                                                updateSettings(false)
                                            }
                                            loading={isUpdatingSettings}
                                        >
                                            Turn off
                                        </AcxButton>
                                    </>
                                ) : (
                                    <AcxButton
                                        onClick={openScheduleModal}
                                        disabled={!hasSelectedHierarchy}
                                        disabledTooltip="Select at least one hierarchy before turning on Proactive Insights."
                                    >
                                        Turn on
                                    </AcxButton>
                                )}
                            </Box>
                        </Box>
                        <Box sx={{ mb: 2, width: "100%" }}>
                            <Typography
                                sx={{ fontWeight: 600, fontSize: "14px" }}
                            >
                                Proactive Insights Hierarchies
                            </Typography>
                            <Box
                                sx={{
                                    alignItems: "center",
                                    columnGap: 2,
                                    display: "flex",
                                    flexWrap: "wrap",
                                    mt: 1,
                                    rowGap: 1.5,
                                    width: "100%",
                                }}
                            >
                                <Box
                                    sx={{
                                        flex: "0 1 340px",
                                        maxWidth: {
                                            xs: "100%",
                                            sm: 340,
                                        },
                                        minWidth: {
                                            xs: "100%",
                                            sm: 280,
                                        },
                                    }}
                                >
                                    <AcxHierarchySelector
                                        displayType="input"
                                        userId={authStore._user.profile.sub}
                                        orgId={id}
                                        setHierarchyIds={
                                            setSelectedHierarchyIds
                                        }
                                        onSaveUpdateWithHierarchies={
                                            applyHierarchyIdsToActiveDefinitions
                                        }
                                        initSelectedHierarchyIds={
                                            selectedHierarchyIds
                                        }
                                        placeholder="Select Hierarchies"
                                    />
                                </Box>
                                {settings?.isEnabled &&
                                    hasSelectedHierarchy &&
                                    runDetails && (
                                        <Box
                                            sx={{
                                                alignItems: "center",
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: 2,
                                            }}
                                        >
                                            <AcxButton
                                                disabled={
                                                    (!runDetails.canRun &&
                                                        !runDetails.canRerun) ||
                                                    isSchedulingRun ||
                                                    isUpdatingHierarchies
                                                }
                                                loading={isSchedulingRun}
                                                tooltip={runButtonTooltip}
                                                onClick={() => {
                                                    if (runDetails.canRerun) {
                                                        setIsRerunConfirmationOpen(
                                                            true,
                                                        );
                                                        return;
                                                    }

                                                    scheduleRun(false);
                                                }}
                                            >
                                                {runDetails.hasRun
                                                    ? "Re-run Proactive Insights"
                                                    : "Run Proactive Insights"}
                                            </AcxButton>
                                            <Box
                                                sx={{
                                                    alignItems: "center",
                                                    display: "flex",
                                                    gap: 0.5,
                                                }}
                                            >
                                                {runDetails.status ===
                                                    "Complete" && (
                                                    <CheckCircleRoundedIcon
                                                        sx={{
                                                            color: theme.palette
                                                                .success[700],
                                                            fontSize: 18,
                                                        }}
                                                    />
                                                )}
                                                {runDetails.isStaleInProgress && (
                                                    <WarningAmberRoundedIcon
                                                        sx={{
                                                            color: theme.palette
                                                                .warning[700],
                                                            fontSize: 18,
                                                        }}
                                                    />
                                                )}
                                                <Typography
                                                    sx={{
                                                        color:
                                                            runDetails.status ===
                                                            "Complete"
                                                                ? theme.palette
                                                                      .success[700]
                                                                : runDetails.isStaleInProgress
                                                                ? theme.palette
                                                                      .warning[700]
                                                                : runDetails.status ===
                                                                  "Failed"
                                                                ? theme.palette
                                                                      .error
                                                                      .main
                                                                : theme.palette
                                                                      .gray[500],
                                                        fontSize: "13px",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {runDetails.isStaleInProgress
                                                        ? `No ${runDetails.status} progress for over 1 hour · ${runPeriod}`
                                                        : runDetails.status ===
                                                          "Complete"
                                                        ? `Completed for ${runPeriod}`
                                                        : runDetails.status ===
                                                          "Failed"
                                                        ? `Run failed for ${runPeriod}`
                                                        : isRunInProgress
                                                        ? `Run in progress for ${runPeriod}`
                                                        : `Not run for ${runPeriod}`}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}
                                {isUpdatingHierarchies && (
                                    <Typography
                                        sx={{
                                            color: theme.palette.gray[500],
                                            fontSize: "14px",
                                        }}
                                    >
                                        Updating Proactive Insights
                                        hierarchies...
                                    </Typography>
                                )}
                            </Box>
                            {!hasSelectedHierarchy && (
                                <Alert severity="warning" sx={{ mt: 1 }}>
                                    Add at least one hierarchy to enable
                                    Proactive Insights.
                                </Alert>
                            )}
                        </Box>
                        <Box
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                minWidth: 0,
                                overflow: "hidden",
                                width: "100%",
                            }}
                        >
                            <AcxDataGrid dataGridStore={dataGridStore} />
                        </Box>
                    </>
                )}
            </Box>
            <AcxDialog
                isOpen={isScheduleModalOpen}
                onClose={() => {
                    if (!isUpdatingSettings) {
                        setIsScheduleModalOpen(false);
                    }
                }}
                buttonsDisabled={isUpdatingSettings}
                title={
                    settings?.isEnabled
                        ? "Update Proactive Insights time"
                        : "Turn on Proactive Insights"
                }
                subTitle="All insights run every Monday in Eastern Time."
                maxWidth="xs"
                fullWidth
                dialogContentChildren={
                    <AcxTextField
                        id="proactive-insights-run-time"
                        type="time"
                        label="Run time"
                        fullWidth
                        required
                        error={false}
                        helperText="Eastern Time"
                        placeholder=""
                        onChange={(event) =>
                            setScheduleTime(event.target.value)
                        }
                        other={{
                            value: scheduleTime,
                            InputLabelProps: { shrink: true },
                            inputProps: { step: 60 },
                        }}
                    />
                }
            >
                <AcxButton
                    onClick={() => updateSettings(true)}
                    loading={isUpdatingSettings}
                    disabled={!scheduleTime}
                    id="confirm-proactive-insights-schedule"
                >
                    {settings?.isEnabled ? "Update time" : "Turn on"}
                </AcxButton>
            </AcxDialog>
            <AcxConfirmationDialog
                isOpen={isRerunConfirmationOpen}
                onClose={() => {
                    if (!isSchedulingRun) {
                        setIsRerunConfirmationOpen(false);
                    }
                }}
                title="Re-run Proactive Insights"
                content={
                    runDetails?.isStaleInProgress
                        ? `This run has had no ${runDetails.status} activity for over one hour. Re-running will delete its ledger and any Proactive Insights generated so far for ${runPeriod}, then start the run again.`
                        : `This will delete the existing Proactive Insights for ${runPeriod} and generate them again.`
                }
                confirmButtonText="Re-run Proactive Insights"
                confirmButtonId="confirm-rerun-proactive-insights"
                onConfirm={() => scheduleRun(true)}
                isLoading={isSchedulingRun}
            />
        </Container>
    );
});
